// src/api/client.ts
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '@/store'

// Расширяем тип для AxiosRequestConfig
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

class ApiClient {
  private client: AxiosInstance
  private refreshTokenPromise: Promise<string> | null = null

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: CustomAxiosRequestConfig) => {
        const token = useAuthStore.getState().accessToken
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // Добавляем язык
        const language = localStorage.getItem('language') || 'ru'
        config.headers['Accept-Language'] = language

        // Шифрование данных (если включено)
        if (import.meta.env.VITE_ENABLE_ENCRYPTION === 'true') {
          config.headers['X-Encrypt'] = 'true'
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig

        // Если 401 и не повторный запрос
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const newToken = await this.refreshAccessToken()
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Если обновление не удалось, выходим
            this.handleLogout()
            return Promise.reject(refreshError)
          }
        }

        // Обработка ошибок
        this.handleError(error)

        return Promise.reject(error)
      }
    )
  }

  private async refreshAccessToken(): Promise<string | null> {
    // Используем Promise для предотвращения множественных запросов
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise
    }

    this.refreshTokenPromise = new Promise(async (resolve, reject) => {
      try {
        const refreshToken = useAuthStore.getState().refreshToken
        if (!refreshToken) {
          reject(new Error('No refresh token'))
          return
        }

        const response = await axios.post(
          `${import.meta.env.VITE_API_URL || '/api'}/v1/auth/refresh`,
          { refresh_token: refreshToken }
        )

        const { access_token, refresh_token } = response.data
        useAuthStore.getState().setTokens(access_token, refresh_token)

        resolve(access_token)
      } catch (error) {
        reject(error)
      } finally {
        this.refreshTokenPromise = null
      }
    })

    return this.refreshTokenPromise
  }

  private handleLogout() {
    useAuthStore.getState().logout()
    window.location.href = '/login'
    toast.error('Session expired. Please login again.')
  }

  private handleError(error: AxiosError) {
    if (error.response) {
      const status = error.response.status
      const data = error.response.data as any
      const detail = data?.detail
      const language = localStorage.getItem('language') || 'ru'
      const translatedDetails: Record<string, Record<string, string>> = {
        'Company INN is required for verification': {
          ru: 'Для проверки компании необходимо указать ИНН.',
          en: 'Enter the company tax ID (INN) before verification.',
          kz: 'Компанияны тексеру үшін БСН енгізіңіз.',
          uk: 'Для перевірки компанії потрібно вказати ІПН.',
          be: 'Для праверкі кампаніі неабходна пазначыць ІПН.',
          uz: 'Kompaniyani tekshirish uchun STIR kiriting.',
          ky: 'Компанияны текшерүү үчүн ИНН киргизиңиз.',
          tg: 'Барои санҷиши ширкат РМА-ро ворид кунед.',
          az: 'Şirkəti yoxlamaq üçün VÖEN daxil edin.',
          hy: 'Ընկերությունը ստուգելու համար մուտքագրեք ՀՎՀՀ-ն։',
        },
      }
      const readableDetail = typeof detail === 'string' ? translatedDetails[detail]?.[language] || detail : detail

      switch (status) {
        case 400:
          toast.error(readableDetail || (language === 'ru' ? 'Некорректный запрос' : 'Bad request'))
          break
        case 401:
          // handled by interceptor
          break
        case 403:
          toast.error(readableDetail || (language === 'ru' ? 'Доступ запрещён' : 'Access denied'))
          break
        case 404:
          toast.error(readableDetail || (language === 'ru' ? 'Ресурс не найден' : 'Resource not found'))
          break
        case 422:
          // Валидационная ошибка - показываем детали
          if (data?.detail) {
            const errors = data.detail
            if (Array.isArray(errors)) {
              errors.forEach((err: any) => {
                toast.error(`${err.loc?.join('.')}: ${err.msg}`)
              })
            } else {
              toast.error(readableDetail)
            }
          }
          break
        case 429:
          toast.error('Too many requests. Please try again later.')
          break
        case 500:
          toast.error(language === 'ru' ? 'Внутренняя ошибка сервера. Попробуйте позже.' : 'Internal server error. Please try again later.')
          break
        default:
          toast.error(readableDetail || (language === 'ru' ? 'Произошла ошибка' : 'An error occurred'))
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.')
    } else {
      toast.error(error.message || 'An error occurred')
    }
  }

  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config)
    return response.data
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config)
    return response.data
  }

  public async upload<T = any>(url: string, file: File, config?: AxiosRequestConfig): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post<T>(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    })
    return response.data
  }
}

export const apiClient = new ApiClient()
